import {
  DEFAULT_RESOURCE_LANGUAGE,
  EducationalResource,
  EducationalResourceAttributes,
  EducationalResourceLanguage,
  EducationalResourcePayload,
  EducationalResourceStatus,
  EducationalResourceType,
  ResourceLanguageCode,
  hasDuration,
  normalizeResourceLanguage,
} from "@/domain/educationalResource.interface";

export type LanguageFormEntry = {
  language: ResourceLanguageCode;
  title: string;
  description: string;
  url: string;
};

export type EducationalResourceFormValues = {
  categoryId: string;
  type: EducationalResourceType;
  status: EducationalResourceStatus;
  thumbnailUrl: string;
  /** Kept as the raw input string; converted to an integer in the payload. */
  duration: string;
  languages: LanguageFormEntry[];
};

export const emptyLanguage = (
  language: ResourceLanguageCode
): LanguageFormEntry => ({ language, title: "", description: "", url: "" });

export const DEFAULT_FORM_VALUES: EducationalResourceFormValues = {
  categoryId: "",
  type: "VIDEO",
  status: "ACTIVE",
  thumbnailUrl: "",
  duration: "",
  languages: [emptyLanguage(DEFAULT_RESOURCE_LANGUAGE)],
};

/** Resource -> form: codes lowercased, English (US) card first (inserted empty if missing). */
export const toEducationalResourceFormValues = (
  resource?: EducationalResource
): EducationalResourceFormValues => {
  if (!resource) {
    return {
      ...DEFAULT_FORM_VALUES,
      languages: [emptyLanguage(DEFAULT_RESOURCE_LANGUAGE)],
    };
  }

  const languages: LanguageFormEntry[] = [];
  (resource.languages || []).forEach((l) => {
    const code = normalizeResourceLanguage(l.language);
    if (!code || languages.some((x) => x.language === code)) return;
    languages.push({
      language: code,
      title: l.title ?? "",
      description: l.description ?? "",
      url: l.url ?? "",
    });
  });

  const english = languages.find(
    (l) => l.language === DEFAULT_RESOURCE_LANGUAGE
  );
  const others = languages.filter(
    (l) => l.language !== DEFAULT_RESOURCE_LANGUAGE
  );

  const duration = resource.attributes?.duration;

  return {
    categoryId: resource.categoryId ?? "",
    type: resource.type ?? DEFAULT_FORM_VALUES.type,
    status: resource.status ?? DEFAULT_FORM_VALUES.status,
    thumbnailUrl: resource.attributes?.thumbnailUrl ?? "",
    duration:
      duration === undefined || duration === null ? "" : String(duration),
    languages: [english ?? emptyLanguage(DEFAULT_RESOURCE_LANGUAGE), ...others],
  };
};

/**
 * Form -> API payload. Strings trimmed, empty description/thumbnail omitted, duration only for
 * VIDEO/AUDIO (BR-10). On edit an empty `attributes: {}` is sent explicitly so that clearing the
 * thumbnail/duration replaces the stored values (BR-11 full replace).
 */
export const toEducationalResourcePayload = (
  values: EducationalResourceFormValues,
  mode: "create" | "edit",
  userId: string
): EducationalResourcePayload => {
  const languages: EducationalResourceLanguage[] = values.languages.map((l) => {
    const entry: EducationalResourceLanguage = {
      language: l.language,
      title: l.title.trim(),
      url: l.url.trim(),
    };
    const description = l.description?.trim();
    if (description) entry.description = description;
    return entry;
  });

  const attributes: EducationalResourceAttributes = {};
  const thumbnailUrl = values.thumbnailUrl?.trim();
  if (thumbnailUrl) attributes.thumbnailUrl = thumbnailUrl;
  const duration = values.duration?.toString().trim();
  if (hasDuration(values.type) && duration !== "" && duration !== undefined) {
    attributes.duration = Number(duration);
  }

  const payload: EducationalResourcePayload = {
    categoryId: values.categoryId,
    type: values.type,
    status: values.status,
    languages,
  };

  if (mode === "edit") {
    payload.attributes = attributes;
    payload.updatedBy = userId;
  } else {
    if (Object.keys(attributes).length) payload.attributes = attributes;
    payload.createdBy = userId;
  }

  return payload;
};
